import {collection, getDocs} from 'firebase/firestore';
import {useCallback, useEffect, useState} from 'react';
import {firebaseFirestore} from '../../config/firebase';

export interface TopicCount {
  topic: string;
  count: number;
}

/**
 * Fetches all seed questions for a module from Firestore and returns counts per topic.
 * Used by AdminSeedQuestionOrphan to show topic distribution and spot wrong/orphan topics.
 */
export function useSeedQuestionTopicCounts(moduleId: string | undefined) {
  const [countsByTopic, setCountsByTopic] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!moduleId) {
      setCountsByTopic(new Map());
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const ref = collection(firebaseFirestore, 'examModules', moduleId, 'seedQuestions');
      const snapshot = await getDocs(ref);
      const counts = new Map<string, number>();
      snapshot.docs.forEach((d) => {
        const topic = (d.data() as {topic?: string}).topic ?? '';
        counts.set(topic, (counts.get(topic) ?? 0) + 1);
      });
      setCountsByTopic(counts);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch seed question counts';
      setError(message);
      setCountsByTopic(new Map());
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const topicCounts: TopicCount[] = Array.from(countsByTopic.entries())
    .map(([topic, count]) => ({topic, count}))
    .sort((a, b) => a.topic.localeCompare(b.topic));

  return {
    countsByTopic,
    topicCounts,
    loading,
    error,
    refetch: fetchCounts,
  };
}
